import { loadProgress, saveProgress, allocateFreeXP } from '../shared/progress';
import { FACTIONS } from '../shared/factions';
import { SKILL_CATALOG } from '../shared/skills';
import { canLevelUp, applyLevelUp, getSkillLevel, levelUpCost, MAX_SKILL_LEVEL, getSkillUpgradePreview } from '../shared/skill-level';
import { getUiSoundPlayer } from './ui-sounds';
import { showLevelUpCelebration } from './level-up-celebration';
import type { FactionId } from '../shared/types';

interface UndoEntry { faction: FactionId; amount: number; }

type Tab = 'allocate' | 'upgrade';

/** Per-level power description for each skill. Index 0 = Lv1, 4 = Lv5. */
const SKILL_LEVEL_DESC: Record<string, string[]> = {
  'meteor-cannon':  ['5 顆子彈', '6 顆子彈', '7 顆子彈 + 爆炸', '8 顆子彈 + 爆炸', '10 顆子彈 + 範圍爆炸'],
  'runic-shield':   ['吸收 3 傷害', '吸收 4 傷害', '吸收 5 傷害', '吸收 6 傷害', '吸收 8 傷害 + 反彈'],
  'frost-cannon':   ['凍結 2 秒', '凍結 2.5 秒', '凍結 3 秒 + 冰刺', '凍結 4 秒 + 冰刺', '凍結 5 秒 + 全凍'],
  'shadow-drone':   ['追加 8 傷害', '追加 12 傷害', '追加 16 傷害 + 穿甲', '追加 20 傷害 + 穿甲', '追加 30 傷害 + 連擊'],
  'eternal-sunshine':['攻速加倍 10 秒', '攻速加倍 12 秒', '攻速加倍 15 秒 + 護甲', '攻速加倍 20 秒 + 護甲', '攻速加倍 30 秒 + 全強化'],
  'spread-shot':    ['3 顆子彈', '4 顆子彈', '5 顆子彈', '6 顆子彈', '7 顆子彈 + 穿透'],
};

export function renderTrainingHall(container: HTMLElement, onBack: () => void): void {
  const root = document.createElement('div');
  root.className = 'training-hall';
  container.appendChild(root);
  const undoStack: UndoEntry[] = [];
  const MAX_UNDO = 3;
  let activeTab: Tab = 'allocate';

  function renderTabBar(): string {
    return `
      <div class="th-tab-bar">
        <button class="th-tab ${activeTab === 'allocate' ? 'th-tab--active' : ''}" data-tab="allocate">分配修煉值</button>
        <button class="th-tab ${activeTab === 'upgrade'  ? 'th-tab--active' : ''}" data-tab="upgrade">強化技能</button>
      </div>
    `;
  }

  function renderAllocatePanel(progress: ReturnType<typeof loadProgress> & object): string {
    const freeXP = (progress as { freeXP: number }).freeXP;
    return `
      <p>自由修煉值剩餘 <strong style="color:#ffd54f;">${freeXP}</strong></p>
      <div class="faction-bar"></div>
      <p style="font-size:13px; color:#88a;">本場戰鬥撤銷次數: ${MAX_UNDO - undoStack.length} / ${MAX_UNDO}</p>
      <div style="display: flex; gap: 8px;">
        <button class="secondary-btn" id="undo" ${undoStack.length === 0 ? 'disabled' : ''}>撤銷上一次</button>
        <button class="primary-btn" id="back">回主畫面</button>
      </div>
    `;
  }

  function renderUpgradePanel(): string {
    return `
      <div class="upgrade-panel"></div>
      <div style="margin-top:16px;">
        <button class="primary-btn" id="back">回主畫面</button>
      </div>
    `;
  }

  function render(): void {
    const progress = loadProgress();
    if (!progress) {
      root.innerHTML = '<p>沒有進度資料 — 請先到修煉學院做一節課。</p>';
      const back = document.createElement('button');
      back.className = 'secondary-btn'; back.textContent = '回主畫面';
      back.addEventListener('click', () => { root.remove(); onBack(); });
      root.appendChild(back);
      return;
    }

    const factions: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

    root.innerHTML = `
      <h1>修煉殿</h1>
      ${renderTabBar()}
      <div class="th-panel-area">
        ${activeTab === 'allocate' ? renderAllocatePanel(progress) : renderUpgradePanel()}
      </div>
    `;

    // Tab switching
    root.querySelectorAll('.th-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        activeTab = (btn as HTMLElement).dataset.tab as Tab;
        render();
      });
    });

    // Back button (present in both panels)
    root.querySelector('#back')?.addEventListener('click', () => {
      root.remove(); onBack();
    });

    if (activeTab === 'allocate') {
      // ── Allocate panel ──────────────────────────────────────────
      const bar = root.querySelector('.faction-bar')!;
      for (const id of factions) {
        const f = FACTIONS[id];
        const row = document.createElement('div');
        row.className = 'faction-row';
        row.dataset.faction = id;
        row.innerHTML = `
          <span class="icon">${f.emoji}</span>
          <span class="name">${f.displayName}</span>
          <span class="xp">${progress.factionXP[id]}</span>
          <span class="alloc">
            <button data-amt="1">+1</button>
            <button data-amt="10">+10</button>
            <button data-amt="50">+50</button>
          </span>
        `;
        row.querySelectorAll('button').forEach(btn => {
          btn.addEventListener('click', () => {
            const amount = parseInt((btn as HTMLElement).dataset.amt!, 10);
            const live = loadProgress()!;
            if (amount > live.freeXP) return;
            getUiSoundPlayer().play('ui-click', 0.3);
            allocateFreeXP(live, id, amount);
            saveProgress(live);
            undoStack.push({ faction: id, amount });
            if (undoStack.length > MAX_UNDO) undoStack.shift();
            render();
          });
        });
        bar.appendChild(row);
      }

      root.querySelector('#undo')!.addEventListener('click', () => {
        const last = undoStack.pop();
        if (!last) return;
        getUiSoundPlayer().play('ui-click', 0.3);
        const live = loadProgress()!;
        live.factionXP[last.faction] -= last.amount;
        live.totalXP -= last.amount;
        live.freeXP += last.amount;
        saveProgress(live);
        render();
      });

    } else {
      // ── Upgrade panel ───────────────────────────────────────────
      const panel = root.querySelector('.upgrade-panel')!;

      // Only show faction skills (not builtin)
      const factionSkills = SKILL_CATALOG.filter(s => s.faction !== 'builtin');

      for (const skill of factionSkills) {
        const lv = getSkillLevel(progress, skill.id);
        const isUnlocked = lv >= 1;
        const isMaxed = lv >= 5;
        const cost = isUnlocked && !isMaxed ? levelUpCost(skill.cost, lv) : null;
        const faction = skill.faction !== 'builtin' ? FACTIONS[skill.faction as FactionId] : null;
        const canUp = isUnlocked && !isMaxed && canLevelUp(progress, skill as { id: string; cost: number; faction: FactionId });
        const powerDesc = isUnlocked
          ? (SKILL_LEVEL_DESC[skill.id]?.[lv - 1] ?? '—')
          : '—';
        const nextPowerDesc = isUnlocked && !isMaxed
          ? (SKILL_LEVEL_DESC[skill.id]?.[lv] ?? '—')
          : null;
        // R-004 A4 — 升級確認介面顯示「前→後關鍵數字」(傷害倍率,唯一參戰乘數)。
        const upgradePreview = isUnlocked && !isMaxed ? getSkillUpgradePreview(lv) : null;

        const card = document.createElement('div');
        card.className = `upgrade-skill-card ${isUnlocked ? '' : 'upgrade-skill-card--locked'}`;
        card.dataset.skillId = skill.id;

        if (!isUnlocked) {
          card.innerHTML = `
            <div class="usc-header">
              <span class="usc-emoji">${faction?.emoji ?? '✨'}</span>
              <span class="usc-name">${skill.displayName}</span>
              <span class="usc-level">未解鎖</span>
            </div>
            <div class="usc-locked-msg">先解鎖才能強化</div>
          `;
        } else {
          card.innerHTML = `
            <div class="usc-header">
              <span class="usc-emoji">${faction?.emoji ?? '✨'}</span>
              <span class="usc-name">${skill.displayName}</span>
              <span class="usc-level">Lv ${lv} / 5${lv >= MAX_SKILL_LEVEL ? ' <span class="usc-lv5-crown">👑</span>' : ''}</span>
            </div>
            <div class="usc-power">目前威力：${powerDesc}</div>
            ${isMaxed
              ? `<div class="usc-max">已達最高等級 ✦</div>`
              : `<div class="usc-cost">Lv ${lv + 1} 需 ${cost} ${faction?.emoji ?? ''}${faction?.displayName ?? ''} 修煉值</div>
                 ${upgradePreview ? `<div class="usc-stat-diff">傷害 ×${upgradePreview.fromDamageMult.toFixed(2)} → <strong>×${upgradePreview.toDamageMult.toFixed(2)}</strong></div>` : ''}
                 ${nextPowerDesc ? `<div class="usc-next">升級後：${nextPowerDesc}</div>` : ''}
                 <button class="usc-upgrade-btn${canUp ? '' : ' usc-upgrade-btn--disabled'}"
                   data-skill-id="${skill.id}"
                   ${canUp ? '' : 'disabled'}>升級</button>`
            }
          `;
        }

        panel.appendChild(card);
      }

      // Wire up upgrade buttons
      panel.querySelectorAll('.usc-upgrade-btn:not([disabled])').forEach(btn => {
        btn.addEventListener('click', () => {
          const skillId = (btn as HTMLElement).dataset.skillId!;
          const skillDef = factionSkills.find(s => s.id === skillId);
          if (!skillDef) return;
          const live = loadProgress()!;
          const oldLv = getSkillLevel(live, skillId);
          const success = applyLevelUp(live, skillDef as { id: string; cost: number; faction: FactionId });
          if (!success) return;
          const newLv = getSkillLevel(live, skillId);

          // Spec §6: full-screen overlay on first-ever level-up OR Lv5 graduation,
          // inline pulse for routine 2nd+ level-ups.
          const isFirstEver = !live.firstLevelUpDone;
          const isMaxGraduation = newLv >= MAX_SKILL_LEVEL;
          const showFullOverlay = isFirstEver || isMaxGraduation;

          if (isFirstEver) live.firstLevelUpDone = true;
          saveProgress(live);
          getUiSoundPlayer().play('unlock-fanfare', 0.5);

          if (showFullOverlay) {
            showLevelUpCelebration(root, skillId, oldLv, newLv, () => render());
          } else {
            render();
            const card = root.querySelector(`.upgrade-skill-card[data-skill-id="${skillId}"]`);
            card?.classList.add('upgrade-skill-card--pulse');
            setTimeout(() => card?.classList.remove('upgrade-skill-card--pulse'), 700);
          }
        });
      });
    }
  }

  render();
}
